// Pet Arena battle math. Pure functions so the rules can be tested headlessly.

import type { PetType } from '@shell/state';

/** who beats whom: key beats value. Star is neutral to everything. */
export const BEATS: Partial<Record<PetType, PetType>> = {
  wave: 'spark', spark: 'leaf', leaf: 'stone', stone: 'frost', frost: 'wave',
};

export const ADVANTAGE = 1.5;
export const DISADVANTAGE = 0.67;
export const LUCKY_CHANCE = 0.1;
export const LUCKY_BONUS = 2;

export type MoveKind = 'tackle' | 'type' | 'big' | 'rest';

export interface MoveDef { kind: MoveKind; label: string; power: number; chargeTurns: number }

export const MOVES: Record<Exclude<MoveKind, 'rest'>, MoveDef> = {
  tackle: { kind: 'tackle', label: 'Tackle', power: 4, chargeTurns: 0 },
  type: { kind: 'type', label: 'Type move', power: 7, chargeTurns: 2 },
  big: { kind: 'big', label: 'Big move', power: 10, chargeTurns: 4 },
};
export const REST_HEAL = 5;

export const TYPE_MOVE_NAMES: Record<PetType, string> = {
  stone: 'Rock Roll', leaf: 'Leaf Whip', spark: 'Zap', wave: 'Splash', frost: 'Snow Puff', star: 'Twinkle',
};
export const BIG_MOVE_NAMES: Record<PetType, string> = {
  stone: 'Boulder Bash', leaf: 'Vine Storm', spark: 'Thunder Hop', wave: 'Tidal Wave', frost: 'Blizzard', star: 'Starfall',
};

export function maxHp(stage: 1 | 2 | 3): number { return 20 + stage * 10; }

/** 1.5 when attacker's type beats defender's, 0.67 when the reverse, else 1. Star is neutral. */
export function multiplier(attacker: PetType, defender: PetType): number {
  if (BEATS[attacker] === defender) return ADVANTAGE;
  if (BEATS[defender] === attacker) return DISADVANTAGE;
  return 1;
}

export function effectiveness(attacker: PetType, defender: PetType): 'super' | 'weak' | 'normal' {
  const m = multiplier(attacker, defender);
  return m > 1 ? 'super' : m < 1 ? 'weak' : 'normal';
}

/**
 * Damage of a move. Tackle is neutral (no type multiplier). Type and Big moves carry the
 * attacker's type. A lucky hit adds a flat bonus; the caller decides luck with the rng.
 */
export function damage(kind: Exclude<MoveKind, 'rest'>, attacker: PetType, defender: PetType, lucky = false): number {
  const base = MOVES[kind].power;
  const mult = kind === 'tackle' ? 1 : multiplier(attacker, defender);
  return Math.max(1, Math.round(base * mult)) + (lucky ? LUCKY_BONUS : 0);
}

export interface Fighter {
  name: string;
  type: PetType;
  stage: 1 | 2 | 3;
  hp: number;
  maxHp: number;
  /** turns until each charged move is ready */
  charge: { type: number; big: number };
  rested: boolean;   // AI: rested once already this fight
}

export function makeFighter(name: string, type: PetType, stage: 1 | 2 | 3): Fighter {
  return { name, type, stage, hp: maxHp(stage), maxHp: maxHp(stage), charge: { type: MOVES.type.chargeTurns, big: MOVES.big.chargeTurns }, rested: false };
}

export function isReady(f: Fighter, kind: Exclude<MoveKind, 'rest'>): boolean {
  if (kind === 'tackle') return true;
  return f.charge[kind] <= 0;
}

/** a turn passes for this fighter: charges tick down by one */
export function tickCharges(f: Fighter) {
  f.charge.type = Math.max(0, f.charge.type - 1);
  f.charge.big = Math.max(0, f.charge.big - 1);
}

/** after using a charged move it starts charging again */
export function spend(f: Fighter, kind: Exclude<MoveKind, 'rest'>) {
  if (kind === 'type') f.charge.type = MOVES.type.chargeTurns;
  if (kind === 'big') f.charge.big = MOVES.big.chargeTurns;
}

export function chargeAll(f: Fighter) { f.charge.type = 0; f.charge.big = 0; }

/** Wild pet AI: rest once when under 30%, otherwise the strongest ready move. */
export function chooseAiMove(f: Fighter): MoveKind {
  if (!f.rested && f.hp < f.maxHp * 0.3) return 'rest';
  if (isReady(f, 'big')) return 'big';
  if (isReady(f, 'type')) return 'type';
  return 'tackle';
}

/** Apply a move from a to b. Returns what happened for the commentary. */
export function applyMove(a: Fighter, b: Fighter, kind: MoveKind, lucky: boolean): { dealt: number; healed: number; eff: 'super' | 'weak' | 'normal' | 'none' } {
  if (kind === 'rest') {
    const before = a.hp;
    a.hp = Math.min(a.maxHp, a.hp + REST_HEAL);
    a.rested = true;
    return { dealt: 0, healed: a.hp - before, eff: 'none' };
  }
  const d = damage(kind, a.type, b.type, lucky);
  b.hp = Math.max(0, b.hp - d);
  spend(a, kind);
  return { dealt: d, healed: 0, eff: kind === 'tackle' ? 'normal' : effectiveness(a.type, b.type) };
}

/** a wild pet never more than one stage above hers */
export function wildStage(herStage: 1 | 2 | 3, roll: number): 1 | 2 | 3 {
  const max = Math.min(3, herStage + 1) as 1 | 2 | 3;
  const s = 1 + Math.floor(roll * max);
  return Math.max(1, Math.min(max, s)) as 1 | 2 | 3;
}
